import { Spinner, Tooltip, useToast } from '@chakra-ui/react';
import { Button } from 'src/components/ui/button';
import SuggestionsList from './suggestions-list';
import { updateSelectedInstances } from 'src/redux/providerBuilder/selectedInstances';
import { getValueByPath } from 'src/utils/provider-builder/instance-selection-utils';
import { checkIfVariableNameExists } from 'src/utils/provider-builder/instance-selection-utils';
import { getFirebaseIdToken } from 'src/utils/utils';
import { fetchPointsOfInterest } from 'src/utils/provider-builder/get-points-of-interests';
import { useEffect, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from 'src/redux/hooks';
import { getSelectedInstances } from 'src/redux/providerBuilder/selectedInstances/selectors';
import {
  getSelectedNetworkIndex,
  getSelectedNetworkRequest,
} from 'src/redux/providerBuilder/selectedNetworkRequest/selectors';
import { Wand2 } from 'lucide-react';

const AiSuggestions = ({
  requestBody,
  contentType,
}: {
  requestBody: string;
  contentType: string;
}) => {
  const [loadingRequests, setLoadingRequests] = useState<Set<string>>(new Set());
  const [dataPoints, setDataPoints] = useState<any[]>([]);
  const dispatch = useAppDispatch();
  const toast = useToast();
  const selectedInstances = useAppSelector(getSelectedInstances);
  const selectedInstancesRef = useRef(selectedInstances);
  const selectedNetworkIndex = useAppSelector(getSelectedNetworkIndex);
  const [openModalId, setOpenModalId] = useState<string | null>(null);
  const selectedRequest = useAppSelector(getSelectedNetworkRequest);
  const requestUrl = selectedRequest[selectedNetworkIndex]?.url || '';

  // console.log('dataPoints', dataPoints);

  useEffect(() => {
    setDataPoints([]); // Clear data points when switching requests
    const storedSuggestions = sessionStorage.getItem(`suggestions_${requestUrl}`);
    if (storedSuggestions) {
      try {
        const parsedSuggestions = JSON.parse(storedSuggestions);
        // Ensure we only show suggestions that match the current URL exactly
        const filteredSuggestions = parsedSuggestions.filter(
          (point: any) =>
            point.requestUrl === requestUrl && point.value && point.shortDescription
        );
        setDataPoints(filteredSuggestions);
      } catch (error) {
        console.error('Error parsing stored suggestions:', error);
        sessionStorage.removeItem(`suggestions_${requestUrl}`);
        getPointsOfInterestUsingAI();
      }
    } else {
      if (!loadingRequests.has(requestUrl)) {
        getPointsOfInterestUsingAI();
      }
    }
  }, [requestBody, contentType, requestUrl]);

  const getPointsOfInterestUsingAI = async () => {
    setLoadingRequests((prev) => new Set(prev).add(requestUrl));
    try {
      // Clear all datapoints when fetching new ones
      setDataPoints([]);

      const accessToken = (await getFirebaseIdToken()) ?? '';

      const result = await fetchPointsOfInterest(requestBody, contentType, accessToken);

      if (!result?.isSuccess) {
        throw new Error('No data points found');
      }

      // Add requestUrl to each datapoint
      const dataPointsWithUrl: any = result.dataPoints.map((point: any) => ({
        ...point,
        requestUrl,
      }));

      sessionStorage.setItem(
        `suggestions_${requestUrl}`,
        JSON.stringify(dataPointsWithUrl)
      );
      setDataPoints(dataPointsWithUrl); // Don't spread previous points

      toast({
        title: 'Success',
        description: `${dataPointsWithUrl.length} points of interest fetched successfully`,
        status: 'success',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } catch (error) {
      console.log({ error });
      toast({
        title: 'Error',
        description: 'Some error occured while fetching points of interest',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setLoadingRequests((prev) => {
        const newSet = new Set(prev);
        newSet.delete(requestUrl);
        return newSet;
      });
    }
  };

  const handleAcceptSuggestion = (data: any) => {
    try {
      if (contentType?.includes('json')) {
        try {
          const responseBody = JSON.parse(requestBody);
          const valueByPath = getValueByPath(responseBody, data.jsonPath.substring(1));
          if (valueByPath?.toString() === data.value) {
            let variableName = data.variableName;
            if (checkIfVariableNameExists(selectedInstancesRef.current, variableName)) {
              const randomNumber = Math.floor(10000 + Math.random() * 90000);
              variableName = `${variableName}_${randomNumber}`;
            }
            const newSelectedInstanceList = {
              data: data.value,
              variableName: variableName,
              aiGenerated: true,
              responseSelection: {
                xPath: '',
                jsonPath: data.jsonPath,
                responseMatch: data.responseMatch,
                invariant: false,
                hash: '',
                matchType: 'greedy',
                description: data.longDescription,
                requestIndex: selectedNetworkIndex,
              },
            };
            dispatch(updateSelectedInstances(newSelectedInstanceList));
            toast({
              title: 'Success',
              description: `${data.value} added successfully`,
              status: 'success',
              duration: 3000,
              isClosable: true,
              position: 'top-right',
            });
          }
        } catch (jsonError) {
          console.error('Error parsing JSON:', jsonError);
          toast({
            title: 'Error',
            description: 'Failed to parse JSON data',
            status: 'error',
            duration: 3000,
            isClosable: true,
            position: 'top-right',
          });
        }
      } else {
        let variableName = data.variableName;
        if (checkIfVariableNameExists(selectedInstancesRef.current, variableName)) {
          const randomNumber = Math.floor(10000 + Math.random() * 90000);
          variableName = `${variableName}_${randomNumber}`;
        }

        const newSelectedInstanceList = {
          data: data.value,
          variableName,
          aiGenerated: true,
          responseSelection: {
            xPath: data.xpath,
            jsonPath: '',
            responseMatch: data.responseMatch,
            invariant: false,
            hash: '',
            matchType: 'greedy',
            description: data.longDescription,
            requestIndex: selectedNetworkIndex,
          },
        };
        console.log('newSelectedInstanceList', newSelectedInstanceList);
        dispatch(updateSelectedInstances(newSelectedInstanceList));
        toast({
          title: 'Success',
          description: `${data.value} added successfully`,
          status: 'success',
          duration: 3000,
          isClosable: true,
          position: 'top-right',
        });
      }
    } catch (error) {
      console.error('Error handling suggestion:', error);
      toast({
        title: 'Error',
        description: 'Failed to process the suggestion',
        status: 'error',
        duration: 3000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setOpenModalId(null);
    }
  };

  const isLoading = loadingRequests.has(requestUrl);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className="text-lg font-semibold">AI Suggestions</h2>
        <Tooltip label="Generate suggestions" placement="top">
          <Button onClick={getPointsOfInterestUsingAI} size="sm">
            {isLoading ? (
              <Spinner size="sm" />
            ) : (
              <div className="flex items-center gap-2">
                <Wand2 className="w-4 h-4" /> Generate
              </div>
            )}
          </Button>
        </Tooltip>
      </div>

      <SuggestionsList
        dataPoints={dataPoints}
        openModalId={openModalId}
        setOpenModalId={setOpenModalId}
        handleAcceptSuggestion={handleAcceptSuggestion}
        isLoading={isLoading}
      />
    </div>
  );
};

export default AiSuggestions;
